import { TransportMode } from '../../../../models/transport-mode.enum';
import { TransportSubMode } from '../../../../models/transport-sub-mode.enum';
import { ClientSideStorageItems, ClientSideStorageService } from '../../../../services/client-side-storage.service';
import LogService from '../../../../services/log-service/log.service';
import { IStopArea } from '../../../../models/stop-area.model';
import { ISettings } from '../models/settings.model';

interface ILegacySettings extends Partial<ISettings> {
  homeStopArea?: IStopArea;
  workStopArea?: IStopArea;
}

function isDefinedStopArea(stopArea: IStopArea | null | undefined): stopArea is IStopArea {
  return stopArea !== null && stopArea !== undefined;
}

function normalizeStopAreaSlots(stopAreas: Array<IStopArea | null | undefined> | undefined, fallbackStopArea?: IStopArea): [IStopArea | undefined, IStopArea | undefined] {
  const definedStopAreas = (stopAreas ?? []).filter(isDefinedStopArea).slice(0, 2);
  if (definedStopAreas.length > 0) return [definedStopAreas[0], definedStopAreas[1]];

  return [fallbackStopArea, undefined];
}

export function getDefinedStopAreas(stopAreas: [IStopArea | undefined, IStopArea | undefined]): IStopArea[] {
  return stopAreas.filter(isDefinedStopArea);
}

function normalizeSettings(settings: ILegacySettings): ISettings {
  const homeStopAreas = normalizeStopAreaSlots(settings.homeStopAreas, settings.homeStopArea);
  const workStopAreas = normalizeStopAreaSlots(settings.workStopAreas, settings.workStopArea);

  return {
    ...settings,
    welcomeCompleted: !!settings.welcomeCompleted,
    enableAutoRefresh: settings.enableAutoRefresh ?? true,
    homeStopAreas,
    workStopAreas,
    showTrips: settings.showTrips ?? true,
    showCommuterParkingLots: settings.showCommuterParkingLots ?? true,
    showTrafficInformation: settings.showTrafficInformation ?? true,
    filterTrafficSituationsByTrips: settings.filterTrafficSituationsByTrips ?? true,
    showOnlyFastestTripPerDeparture: settings.showOnlyFastestTripPerDeparture ?? false,
    showLeavesIn: settings.showLeavesIn ?? false,
    showDepartureTime: settings.showDepartureTime ?? true,
    showOriginStopName: settings.showOriginStopName ?? true,
    showOnlyFirstBadgeInSummary: settings.showOnlyFirstBadgeInSummary ?? false,
    transportModes: settings.transportModes ?? [TransportMode.tram, TransportMode.bus, TransportMode.train, TransportMode.ferry],
    transportSubModes: settings.transportSubModes ?? [TransportSubMode.vasttagen, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain],
  };
}

export class SettingsHelper {
  public static areFunctionalCookiesAllowed(): boolean {
    return ClientSideStorageService.isConsentGivenFor(ClientSideStorageItems.LocalStorage.LabsMyStopAreasSettings);
  }

  public static getSettings(): ISettings {
    const defaults = normalizeSettings({
      welcomeCompleted: false,
      enableAutoRefresh: true,
      homeStopAreas: [undefined, undefined],
      workStopAreas: [undefined, undefined],
      showTrips: true,
      showOnlyFastestTripPerDeparture: false,
      showCommuterParkingLots: true,
      showTrafficInformation: true,
      filterTrafficSituationsByTrips: true,
      showLeavesIn: false,
      showDepartureTime: true,
      showOriginStopName: true,
      showOnlyFirstBadgeInSummary: false,
      transportModes: [TransportMode.tram, TransportMode.bus, TransportMode.train, TransportMode.ferry],
      transportSubModes: [TransportSubMode.vasttagen, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain],
    });
    const settingsJson = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.LabsMyStopAreasSettings);
    if (settingsJson) {
      try {
        return normalizeSettings({ ...defaults, ...(JSON.parse(settingsJson) as ILegacySettings) });
      } catch {
        ClientSideStorageService.clearLocalStorage(ClientSideStorageItems.LocalStorage.LabsMyStopAreasSettings);
        LogService.logError('Error parsing My Stop Areas settings from local storage. Clearing corrupted data.');
        // Return default settings below
      }
    }
    return defaults;
  }

  public static saveSettings(settings: ISettings): void {
    ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.LabsMyStopAreasSettings, JSON.stringify(normalizeSettings(settings)));
  }

  public static getHiddenTrafficSituationNumbers(): string[] {
    const json = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.LabsHiddenTrafficSituations);
    if (json) {
      try {
        return JSON.parse(json) as string[];
      } catch {
        ClientSideStorageService.clearLocalStorage(ClientSideStorageItems.LocalStorage.LabsHiddenTrafficSituations);
        LogService.logError('Error parsing hidden traffic situation numbers from local storage. Clearing corrupted data.');
        // Return empty array below
      }
    }
    return [];
  }

  public static saveHiddenTrafficSituationNumbers(situationNumbers: string[]): void {
    ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.LabsHiddenTrafficSituations, JSON.stringify(situationNumbers));
  }
}
